// Visual tokens for rendered doclets, from the World 50 Design Guidelines v2.0
// (September 2026). Comments name the guide's CSS custom properties, so values
// can be checked against it or swapped for the shared stylesheet later. This
// replaces the earlier legacy (Akzidenz / Baskerville) doclet style.
//
// Fonts: Oswald for display, Inter for everything else, loaded from Google
// Fonts in web/index.html.

export const fonts = {
  // --font-display
  display: '"Oswald", Arial, sans-serif',
  // --font-sans
  sans: '"Inter", -apple-system, "Segoe UI", Arial, sans-serif'
}

export const colors = {
  // --text-primary (Brand Black)
  text: '#141413',
  // --text-secondary (ink-700)
  textSecondary: '#686764',
  // --text-tertiary (ink-500): labels and captions only
  textTertiary: '#8C8B88',
  // --surface-page
  page: '#FFFFFF',
  // --surface-sunken (ink-200): the highlighted band
  band: '#EDEDED',
  // --accent (Sky Blue): links
  link: '#109EDA',
  // --border-default (ink-300)
  rule: '#D3D2D1'
}

// --space-*
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  6: 24,
  8: 32,
  12: 48,
  16: 64,
  24: 96
}

export const layout = {
  // The doclet page column (kept from legacy's 54em). Blocks fill it, so this
  // alone sets the reading width.
  pageMaxWidth: 864,
  // Page padding around the blocks
  blockPadding: space[4],
  // Inset inside a highlighted band
  bandPadding: space[6]
}

// Doclets compare against the width of the page itself, not the viewport, so
// they render the same inside the builder canvas.
export const breakpoints = {
  wide: 768
}

export const typography = {
  // Title block title: Display 1 -- Oswald 400, 42px / 110%, uppercase
  // ("Display is uppercase, always"). Narrow pages step down to 32px.
  title: (isWide: boolean) => {
    const fontSize = isWide ? 42 : 32
    return {
      fontFamily: fonts.display,
      fontWeight: '400' as const,
      color: colors.text,
      fontSize,
      lineHeight: Math.round(fontSize * 1.1),
      letterSpacing: fontSize * 0.005,
      textTransform: 'uppercase' as const,
      marginBottom: space[2]
    }
  },
  // Title block subtitle: H1 -- Inter 500, 24px / 150%, in --text-secondary:
  // the Inter line beneath a single Oswald title
  subtitle: (isWide: boolean) => {
    const fontSize = isWide ? 24 : 20
    return {
      fontFamily: fonts.sans,
      fontWeight: '500' as const,
      color: colors.textSecondary,
      fontSize,
      lineHeight: fontSize * 1.5,
      marginBottom: space[6]
    }
  },
  // Text block body: Body LG -- Inter 400, 16px / 150% ("body leading stays at
  // 150%"). The same at every width.
  // Same signature as the width-dependent styles; this one doesn't vary
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  body: (_isWide: boolean) => ({
    fontFamily: fonts.sans,
    fontWeight: '400' as const,
    color: colors.text,
    fontSize: 16,
    lineHeight: 24,
    marginBottom: space[4]
  }),
  // Bold inside Text: Inter semibold
  bold: { fontWeight: '600' as const },
  // Heading style inside Text: H1 -- Inter 500, 24px / 150%
  heading: (isWide: boolean) => {
    const fontSize = isWide ? 24 : 20
    return {
      fontFamily: fonts.sans,
      fontWeight: '500' as const,
      color: colors.text,
      fontSize,
      lineHeight: fontSize * 1.5,
      marginBottom: space[2]
    }
  },
  // Sub-Heading style inside Text: H2 / Title LG -- Inter 600, 16px / 150%
  // Same signature as the width-dependent styles; this one doesn't vary
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subheading: (_isWide: boolean) => ({
    fontFamily: fonts.sans,
    fontWeight: '600' as const,
    color: colors.text,
    fontSize: 16,
    lineHeight: 24,
    marginBottom: space[2]
  }),
  // Small print style inside Text (footnotes): Caption -- Inter 400,
  // 13px / 20px, in --text-secondary
  // Same signature as the width-dependent styles; this one doesn't vary
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  small: (_isWide: boolean) => ({
    fontFamily: fonts.sans,
    fontWeight: '400' as const,
    color: colors.textSecondary,
    fontSize: 13,
    lineHeight: 20,
    marginBottom: space[4]
  }),
  // ul / ol
  // Same signature as the width-dependent styles; this one doesn't vary
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  list: (_isWide: boolean) => ({
    indent: space[6],
    itemSpacing: space[2]
  }),
  // Image captions: Caption -- Inter 400, 13px / 20px, secondary
  caption: {
    fontFamily: fonts.sans,
    fontWeight: '400' as const,
    color: colors.textSecondary,
    fontSize: 13,
    lineHeight: 20,
    marginTop: space[2]
  },
  // The confidentiality line: Caption -- Inter 400, 13px / 20px, tertiary
  footer: {
    fontFamily: fonts.sans,
    fontWeight: '400' as const,
    color: colors.textTertiary,
    fontSize: 13,
    lineHeight: 20,
    textAlign: 'right' as const
  }
}
